import { useEffect, useRef, useState } from 'react';
import i18n, { type Resource } from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import otaClient from '@crowdin/ota-client';
import { queryParser } from '@applyft-web/utils';
import { CROWDIN_DIST, IS_PROD } from '../core/constants';
import en from './en.json';
import { setAmplitudeUserProperties } from '../analytics';
import { getCurrentLanguageItem, getSearchLanguage, parseOnboardingPath } from './languageConfig';

// types
type LocalesMap = Record<string, string>;
type TranslationsCache = { ts: number; files: Record<string, object> };
interface UseInitLocalesProps {
  ready: boolean;
  isRtl: boolean;
}

// constants
const FALLBACK = 'en';
const LS_KEY_TRANSLATIONS = 'i18n.translations';
const LS_KEY_LOCALE = 'i18n.locale';
const client = CROWDIN_DIST ? new otaClient(CROWDIN_DIST) : null;
const inflightRes = new Map<string, Promise<object>>();
let localesCache: Promise<LocalesMap> | null = null;

function normalizeDocumentLangForCookieYes(lng: string): string {
  const norm = lng.replace('_', '-').toLowerCase();

  if (norm.startsWith('zh')) {
    if (norm === 'zh-cn') return 'zh-hant';
    if (norm === 'zh-tw') return 'zh-hans';
  }

  return lng;
}

// utils
function resolveLang(requested: string, localesList: string[]): string {
  const { custom_lng } = queryParser(window.location.search);
  if (!IS_PROD && custom_lng) return custom_lng;
  const normalize = (lng: string) => lng.replace('_', '-').toLowerCase(); // e.g. en_US -> en-us
  const baseOf = (lng: string) => normalize(lng).split('-')[0];
  let req = normalize(requested);
  const b = baseOf(req);
  if (b === 'nb' || b === 'nn') req = 'no';
  const available = localesList.map(normalize);

  const exactIdx = available.indexOf(req);
  if (exactIdx >= 0) return localesList[exactIdx];

  // e.g. "en-us" → "en"
  const base = baseOf(req);
  const baseIdx = available.indexOf(base);
  if (baseIdx >= 0) return localesList[baseIdx];

  // e.g. non-exist "en-US" → "en-UK"
  const variantIdx = available.findIndex((k) => baseOf(k) === base);
  if (variantIdx >= 0) return localesList[variantIdx];

  return FALLBACK;
}
function readLS<T>(key: string): T | null {
  if (typeof window === 'undefined') return null;
  try {
    return JSON.parse(localStorage.getItem(key) || 'null');
  } catch {
    return null;
  }
}
function writeLS<T>(key: string, value: T) {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* ignore quota */
  }
}

// requests
async function getLocalesList(): Promise<LocalesMap> {
  if (!client) return {};
  if (localesCache) return localesCache;

  localesCache = client
    .getContent()
    .then((content) => {
      if (!content || typeof content !== 'object' || Array.isArray(content)) {
        return {};
      }
      return Object.fromEntries(Object.entries(content).map(([lng, urls]) => [lng, urls[0]]));
    })
    .catch((error) => {
      console.error(error);
      return {};
    });

  return localesCache;
}
async function getRes(file?: string) {
  if (!file || !client) return {};

  try {
    const ts = await client.getManifestTimestamp();

    const cached = readLS<TranslationsCache>(LS_KEY_TRANSLATIONS);
    if (cached && cached.ts === ts && cached.files[file]) {
      return cached.files[file];
    }

    const p = inflightRes.get(file);
    if (p) return p;

    const req = client
      .getFileTranslations(file)
      .then((tr) => (tr && typeof tr === 'object' ? tr : {}))
      .catch((e) => {
        console.error(e);
        return {};
      })
      .finally(() => inflightRes.delete(file));

    inflightRes.set(file, req);

    const translations = await req;

    const base = readLS<TranslationsCache>(LS_KEY_TRANSLATIONS);
    const files = base && base.ts === ts ? base.files : {};
    writeLS<TranslationsCache>(LS_KEY_TRANSLATIONS, {
      ts,
      files: { ...files, [file]: translations },
    });

    return translations;
  } catch (e) {
    console.error(e);
    const cached = readLS<TranslationsCache>(LS_KEY_TRANSLATIONS);
    return cached?.files?.[file] ?? {};
  }
}

export async function setAppLanguage(localeKey: string): Promise<void> {
  const content = await getLocalesList();
  const target = resolveLang(localeKey, Object.keys(content));
  const resByTarget = await getRes(content[target]);
  const hasTargetRes = Object.keys(resByTarget).length > 0;

  if (hasTargetRes && !i18n.hasResourceBundle(target, 'translation')) {
    i18n.addResourceBundle(target, 'translation', resByTarget, true, true);
  }
  await i18n.changeLanguage(hasTargetRes ? target : FALLBACK);
  writeLS(LS_KEY_LOCALE, localeKey);
  setAmplitudeUserProperties({ display_language: i18n.language ?? FALLBACK });
}

const getLinkLanguage = () =>
  parseOnboardingPath(window.location.pathname).language ??
  getSearchLanguage(window.location.search);

// ponytail: the saved pick is read on render, not subscribed to. Switching between the two
// English rows changes no i18n language, so callers re-render through their own state
// (the language modal closing). Move it to the store if something else has to react.
export function useCurrentLanguageItem() {
  const { i18n: instance } = useTranslation();
  return getCurrentLanguageItem(
    instance.language,
    getLinkLanguage() || readLS<string>(LS_KEY_LOCALE),
    navigator.language,
  );
}

// TODO: Consider to move into shareable package
export function useInitLocales(): UseInitLocalesProps {
  const [isRtl, setIsRtl] = useState(false);
  const [ready, setReady] = useState(false);
  const [content, setContent] = useState<Record<string, string> | undefined>();
  const didRun = useRef(false);

  useEffect(() => {
    if (content) return;

    let aborted = false;
    getLocalesList().then((languages) => {
      if (!aborted) setContent(languages);
    });

    return () => {
      aborted = true;
    };
  }, [content]);

  useEffect(() => {
    if (didRun.current || !content) return;
    didRun.current = true;

    let aborted = false;

    (async () => {
      const raw =
        getLinkLanguage() ||
        readLS<string>(LS_KEY_LOCALE) ||
        (typeof navigator !== 'undefined' ? navigator.language : FALLBACK);
      const target = resolveLang(raw, Object.keys(content));
      const resByTarget = await getRes(content[target]);
      const hasTargetRes = target !== 'en' && Object.keys(resByTarget).length > 0;

      const startResources: Resource = {
        en: { translation: en },
      };
      if (hasTargetRes) {
        startResources[target] = { translation: resByTarget };
      }

      if (!i18n.isInitialized) {
        await i18n.use(initReactI18next).init({
          fallbackLng: FALLBACK,
          lng: hasTargetRes ? target : FALLBACK,
          resources: startResources,
          interpolation: { escapeValue: false },
          react: { useSuspense: false },
          debug: !IS_PROD,
        });
      } else {
        if (hasTargetRes && !i18n.hasResourceBundle(target, 'translation')) {
          i18n.addResourceBundle(target, 'translation', resByTarget, true, false);
        }
        if (hasTargetRes && i18n.language !== target) {
          await i18n.changeLanguage(target);
        }
      }

      const dir = i18n.dir(i18n.language ?? FALLBACK);
      if (typeof document !== 'undefined') {
        document.documentElement.lang = normalizeDocumentLangForCookieYes(
          i18n.language ?? FALLBACK,
        );
      }
      setAmplitudeUserProperties({ display_language: i18n.language ?? FALLBACK });

      if (aborted) return;

      setIsRtl(dir === 'rtl');
      setReady(true);
    })().catch((err) => {
      if (!IS_PROD) console.error('i18n init failed:', err);
      if (!aborted) setReady(true);
    });

    return () => {
      aborted = true;
    };
  }, [content]);

  useEffect(() => {
    if (!ready) return;

    const onLang = (lng: string) => {
      setIsRtl(i18n.dir(lng) === 'rtl');
      document.documentElement.lang = normalizeDocumentLangForCookieYes(lng);
    };

    i18n.on('languageChanged', onLang);
    return () => {
      i18n.off('languageChanged', onLang);
    };
  }, [ready]);

  return { ready, isRtl };
}
